import { ContinueToNextViewButton } from "@faire/deprecated-retailer-visitor-shared/components/AppSignIn/components/ContinueToNextViewButton";
import { convertErrorToLoginError } from "@faire/deprecated-retailer-visitor-shared/components/AppSignIn/components/convertErrorToLoginError";
import { LoginWrapper } from "@faire/deprecated-retailer-visitor-shared/components/AppSignIn/components/styled";
import { View } from "@faire/deprecated-retailer-visitor-shared/components/AppSignIn/components/View";
import { useHCaptchaLogin } from "@faire/deprecated-retailer-visitor-shared/hooks/hCaptcha/useHCaptchaLogin";
import { MIN_PASSWORD_LENGTH } from "@faire/deprecated-retailer-visitor-shared/lib/consts";
import { useSSOPasswordStore } from "@faire/deprecated-retailer-visitor-shared/stores/domain/SSO/useSSOPasswordStore";
import { useSSOStore } from "@faire/deprecated-retailer-visitor-shared/stores/domain/SSO/useSSOStore";
import { Core } from "@faire/design-tokens";
import { trackSigninTriggersMfaSubmit } from "@faire/web-api--source/events/signin/submit/triggersMfa";
import { IImage } from "@faire/web-api--source/indigofair/data/IImage";
import { ILoginRequest } from "@faire/web-api--source/indigofair/data/ILoginRequest";
import { ILoginResponse } from "@faire/web-api--source/indigofair/data/ILoginResponse";
import { IUser } from "@faire/web-api--source/indigofair/data/IUser";
import { LoginState } from "@faire/web-api--source/indigofair/data/LoginState";
import { useStrictLocalization } from "@faire/web/common/localization";
import { Input } from "@faire/web/slate-deprecated/Input";
import { Avatar } from "@faire/web/slate/deprecated/Avatar";
import { Eye, EyeClosed } from "@faire/web/slate/icons/foundation/Eye";
import { Column } from "@faire/web/slate/Layout";
import { Spacer } from "@faire/web/slate/spacing";
import { Typography } from "@faire/web/slate/Typography";
import { EmbeddedLink } from "@faire/web/ui/EmbeddedLink";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import React, { FC, useCallback, useState, type JSX } from "react";
import styled from "styled-components";

interface IProps {
  email: string;
  profileImage?: IImage;
  userName?: string;
  onComplete: (result: ConfirmPasswordResult) => void;
  onLogin: (user: IUser) => void;
}

type ConfirmPasswordResult = {
  nextView: View;
  mfaDescription?: string;
  mfaTitle?: string;
  mfaOptions?: ILoginResponse.IMfaOption[];
  password?: string;
};

export const ConfirmPasswordView: FC<IProps> = ({
  email,
  profileImage,
  userName,
  onComplete,
  onLogin,
}) => {
  const { strictLocalize } = useStrictLocalization();
  const [password, setPassword] = useState("");
  const [hidePassword, setHidePassword] = useState(true);
  const [inputError, setInputError] = useState<string>();
  const [error, setError] = useState("");

  const { ssoToken } = useSSOStore();
  const { setShowConfirmPasswordView } = useSSOPasswordStore();

  const [isLoading, setIsLoading] = useState(false);
  const { hCaptchaLogin } = useHCaptchaLogin();

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(event.currentTarget?.value ?? "");
    setInputError(undefined);
  };

  const toggleHidePassword = () => setHidePassword(!hidePassword);

  const onContinue = async () => {
    if (password.length < MIN_PASSWORD_LENGTH) {
      setInputError(
        strictLocalize({
          id: "LS2IIS",
          defaultMessage: "Password too short",
        })
      );
      return;
    }
    try {
      const loginRequest = ILoginRequest.build({
        sso_token: ssoToken,
        password,
        enable_mfa_method_selection: true,
      });

      setIsLoading(true);
      const { user, mfa_description, mfa_title, mfa_options, login_state } =
        await hCaptchaLogin(loginRequest);

      if (login_state === LoginState.MFA_METHOD_SELECTION_REQUIRED) {
        trackSigninTriggersMfaSubmit("CONFIRM_PASSWORD");
        onComplete({
          nextView: View.SELECT_MFA_OPTION,
          password,
          mfaOptions: mfa_options,
          mfaTitle: mfa_title,
          mfaDescription: mfa_description,
        });
        return;
      }

      if (login_state === LoginState.MFA_REQUIRED) {
        trackSigninTriggersMfaSubmit("CONFIRM_PASSWORD");
        onComplete({
          nextView: View.ENTER_MFA,
          password,
          mfaDescription: mfa_description,
          mfaTitle: mfa_title,
        });
        return;
      }

      if (!user) {
        setError(
          strictLocalize({
            id: "bw8Bnw",
            defaultMessage: "Invalid credentials",
            description: {
              text: "Email or password that was entered is incorrect",
            },
          })
        );
        return;
      }

      setShowConfirmPasswordView(false);
      onLogin(user);
      onComplete({
        nextView: View.LOADING,
      });
    } catch (e) {
      const loginError = convertErrorToLoginError(e, strictLocalize);
      if (loginError.httpStatusCode === 429) {
        setError(
          strictLocalize({
            id: "wzldCA",
            defaultMessage: "Exceeded maximum allowed number of login attempts",
            description: {
              text: "Error that shows when a user has attempted to log in too many times",
            },
          })
        );
      } else {
        setError(
          loginError.message ??
            strictLocalize({
              id: "8GvVWZ",
              defaultMessage: "Something went wrong, please try again.",
            })
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const LinkFormatting = useCallback(
    (chunks: JSX.Element) => (
      <EmbeddedLink
        onClick={() => onComplete({ nextView: View.RESET_PASSWORD })}
      >
        {chunks}
      </EmbeddedLink>
    ),
    [onComplete]
  );

  return (
    <LoginWrapper>
      {!!profileImage?.url || userName ? (
        <Column align="flex-start">
          <Avatar image={profileImage} size="medium" userName={userName} />
        </Column>
      ) : null}
      <Spacer height="2x" />
      <Typography variant="pageHeaderSerifRegular">
        <StrictLocalMsg
          id="nl2qUm"
          defaultMessage="Confirm Faire password"
          description={{
            img: "https://cdn.faire.com/fastly/f8319f8a846f05863cef7324f31b26efa0a8c9761a2383ed7860fa56bb21566b.png",
          }}
        />
      </Typography>
      <Spacer height="1x" />
      <Typography>
        <StrictLocalMsg
          id="SaQ8Se"
          defaultMessage="Continue as {retailerEmailAddress}"
          description={{
            img: "https://cdn.faire.com/fastly/f8319f8a846f05863cef7324f31b26efa0a8c9761a2383ed7860fa56bb21566b.png",
          }}
          values={{ retailerEmailAddress: email }}
        />
      </Typography>
      {error ? (
        <>
          <Spacer height="2x" />
          <Typography color={Core.text.critical}>{error}</Typography>
        </>
      ) : null}
      <Spacer height="3x" />
      <Input
        label={strictLocalize({
          id: "A3LsA2",
          defaultMessage: "Enter Password",
          description: {
            img: "https://cdn.faire.com/fastly/f8319f8a846f05863cef7324f31b26efa0a8c9761a2383ed7860fa56bb21566b.png",
          },
        })}
        value={password}
        name="enter_password"
        type={hidePassword ? "password" : undefined}
        error={inputError}
        onChange={handleInputChange}
        suffixNode={
          <button onClick={toggleHidePassword}>
            {hidePassword ? (
              <Eye fontSize="16px" />
            ) : (
              <EyeClosed fontSize="16px" />
            )}
          </button>
        }
      />
      <Spacer height="2x" />
      <Typography>
        <StrictLocalMsg
          id="sSUqKx"
          defaultMessage="Forgot your password? <LinkFormatting>Reset It</LinkFormatting>"
          description={{
            img: "https://cdn.faire.com/fastly/f8319f8a846f05863cef7324f31b26efa0a8c9761a2383ed7860fa56bb21566b.png",
          }}
          values={{
            LinkFormatting,
          }}
        />
      </Typography>
      <Spacer height="3x" />
      <Continue onClick={onContinue} isLoading={isLoading} />
    </LoginWrapper>
  );
};

const Continue = styled(ContinueToNextViewButton)`
  width: 100%;
`;
